import React, {useMemo, useState} from 'react';
import {Alert, Button, Card, Container, Form, Spinner} from 'react-bootstrap';
import {UserService} from '../../../service/users.service';
import {formatRut, validarRut} from '../../../service/utils/constants';

interface RegisterUserProps {
    onCreated?: (result: {rut: string; name: string; defaultPassword: string}) => void;
}

const emptyForm = {rut: '', name: '', email: ''};

const RegisterUser: React.FC<RegisterUserProps> = ({onCreated}) => {
    const [form, setForm] = useState(emptyForm);
    const [touched, setTouched] = useState<{ [key in keyof typeof emptyForm]?: boolean }>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [created, setCreated] = useState<{rut: string; name: string; defaultPassword: string} | null>(null);

    const rutValido = useMemo(() => {
        if (!form.rut.trim()) return false;
        return validarRut(form.rut);
    }, [form.rut]);

    const defaultPasswordPreview = useMemo(
        () => form.rut.replace(/[.\-]/g, '').trim(),
        [form.rut],
    );

    const emailValida = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

    const isFormValid = rutValido && !!form.name.trim() && emailValida;

    const handleChange = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({...prev, [field]: e.target.value}));
    };

    const handleRutBlur = () => {
        setTouched((prev) => ({...prev, rut: true}));
        if (validarRut(form.rut)) {
            setForm((prev) => ({...prev, rut: formatRut(prev.rut)}));
        }
    };

    const handleReset = () => {
        setForm(emptyForm);
        setTouched({});
        setError('');
        setCreated(null);
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({rut: true, name: true, email: true});
        setError('');

        if (!isFormValid) {
            setError('Revisa los campos marcados en rojo antes de continuar.');
            return;
        }

        const organization = localStorage.getItem('organization');
        if (!organization) {
            setError('No se detectó tu organización actual. Vuelve a iniciar sesión e intenta de nuevo.');
            return;
        }

        setSaving(true);
        try {
            const rut = formatRut(form.rut);
            await UserService.register({
                email: form.email.trim(),
                rut,
                name: form.name.trim(),
                organization,
            });
            const result = {rut, name: form.name.trim(), defaultPassword: rut.replace(/[.\-]/g, '')};
            setCreated(result);
            onCreated?.(result);
        } catch (err) {
            setError('No se pudo registrar el usuario. Verifica que el RUT o el email no estén ya en uso.');
        } finally {
            setSaving(false);
        }
    };

    if (created) {
        return (
            <Container className="d-flex justify-content-center align-items-center py-4">
                <Card style={{width: '26rem'}} className="shadow-sm">
                    <Card.Body>
                        <Alert variant="success" className="mb-3">
                            <Alert.Heading className="fs-6">Usuario creado correctamente</Alert.Heading>
                            <p className="mb-1">
                                <strong>{created.name}</strong> ({created.rut})
                            </p>
                            <p className="mb-0">
                                Contraseña por defecto: <code>{created.defaultPassword}</code>
                            </p>
                        </Alert>
                        <p className="text-muted small">
                            Comunícale al usuario su RUT y esta contraseña para que pueda iniciar sesión.
                        </p>
                        <Button className="w-100" onClick={handleReset}>
                            Registrar otro usuario
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="d-flex justify-content-center align-items-center py-4">
            <Card style={{width: '26rem'}} className="shadow-sm">
                <Card.Body>
                    <Card.Title className="text-center mb-1">Creación rápida de usuario</Card.Title>
                    <p className="text-muted text-center small mb-4">
                        Solo 3 datos para registrar a un trabajador. Podrás completar el resto de su información más
                        tarde.
                    </p>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form onSubmit={handleRegister} noValidate>
                        <Form.Group className="mb-3">
                            <Form.Label>RUT *</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.rut}
                                onChange={handleChange('rut')}
                                onBlur={handleRutBlur}
                                placeholder="Ej: 12345678-9"
                                isInvalid={!!touched.rut && !rutValido}
                                isValid={!!touched.rut && rutValido}
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                RUT inválido. Formato esperado: 12345678-9.
                            </Form.Control.Feedback>
                            {rutValido && defaultPasswordPreview && (
                                <Form.Text className="text-muted">
                                    La contraseña por defecto será <code>{defaultPasswordPreview}</code>
                                </Form.Text>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.name}
                                onChange={handleChange('name')}
                                onBlur={() => setTouched((prev) => ({...prev, name: true}))}
                                placeholder="Nombre completo"
                                isInvalid={!!touched.name && !form.name.trim()}
                            />
                            <Form.Control.Feedback type="invalid">Ingresa un nombre.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Correo electrónico *</Form.Label>
                            <Form.Control
                                type="email"
                                value={form.email}
                                onChange={handleChange('email')}
                                onBlur={() => setTouched((prev) => ({...prev, email: true}))}
                                placeholder="correo@ejemplo.com"
                                isInvalid={!!touched.email && !emailValida}
                            />
                            <Form.Control.Feedback type="invalid">Ingresa un correo válido.</Form.Control.Feedback>
                        </Form.Group>
                        <Button type="submit" className="w-100" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" /> Registrando...
                                </>
                            ) : (
                                'Registrar'
                            )}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default RegisterUser;
